/**
 * Incluir e alterar a caixa de fusíveis.
 *
 * **É o `CaixaFusiveis/Incluir` do Sistema de hoje**, campo por campo: título,
 * modo, componente, modelo(s) e sistema. O legado grava a caixa e depois insere
 * uma linha por modelo escolhido; aqui é a mesma sequência, em duas chamadas.
 *
 * **A mesma caixa física existe uma vez por modelo.** Por isso o modelo é
 * múltiplo e não único: escolher três modelos aqui é o gesto que, no acervo,
 * vira três linhas ligadas a esta caixa. No projeto 550 são 120 caixas para 7
 * títulos.
 *
 * **Componente e sistema são obrigatórios, e isso veio do dado**: das 1.755
 * caixas de produção, nenhuma está sem um ou sem outro. Na alteração o campo
 * vazio seria pior que recusa — apagaria o vínculo que já está lá.
 *
 * **O modo muda o que acontece depois de gravar.** "Armazena em banco" faz o
 * sistema antigo gerar a tabela na hora (`caixaFusiveis.Gerar()`); aqui quem
 * gera é o botão Regenerar, que já existe e diz o que vai fazer antes.
 */

import { useEffect, useState } from "react"
import {
  type CaixaDeFusiveis, usarExcluirCaixa, usarSalvarCaixa,
} from "@/api/caixasFusiveis"
import { usarComponentes } from "@/api/catalogos"
import { usarSistemasDeConteudo } from "@/api/catalogosDeConteudo"
import { usarModelosDoProjeto } from "@/api/modelos"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

/** Os dois modos do cadastro antigo, com as palavras dele. */
const MODOS = [
  { valor: 0, rotulo: "Gera todas as vezes" },
  { valor: 1, rotulo: "Armazena em banco de dados" },
]

function recado(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return e instanceof Error ? e.message : "Não foi possível gravar."
}

const campo: React.CSSProperties = {
  width: "100%", padding: "5px 7px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}

const rotulo: React.CSSProperties = {
  fontSize: 10, color: "var(--txt3)", textTransform: "uppercase", letterSpacing: 0.4,
}

export function FormularioDaCaixa({ projetoId, caixa, aoFechar, aoAvisar }: {
  projetoId: number
  /** `null` cria; uma caixa abre para alterar. */
  caixa: CaixaDeFusiveis | null
  aoFechar: () => void
  aoAvisar: (texto: string) => void
}) {
  const componentes = usarComponentes(projetoId)
  const sistemas = usarSistemasDeConteudo()
  const modelos = usarModelosDoProjeto(projetoId)
  const salvar = usarSalvarCaixa(projetoId)
  const excluir = usarExcluirCaixa(projetoId)

  const [titulo, setTitulo] = useState("")
  const [modo, setModo] = useState(0)
  const [componenteId, setComponenteId] = useState(0)
  const [sistemaId, setSistemaId] = useState(0)
  const [escolhidos, setEscolhidos] = useState<Set<number>>(new Set())
  const [confirmando, setConfirmando] = useState(false)

  useEffect(() => {
    setTitulo(caixa?.titulo ?? "")
    setModo(caixa?.modo ?? 0)
    setComponenteId(caixa?.componenteId ?? 0)
    setSistemaId(caixa?.sistemaId ?? 0)
    setEscolhidos(new Set(caixa?.modelos ?? []))
    setConfirmando(false)
  }, [caixa])

  const faltando: string[] = []
  if (titulo.trim() === "") faltando.push("o título")
  if (componenteId <= 0) faltando.push("o componente")
  if (sistemaId <= 0) faltando.push("o sistema")

  function gravar() {
    if (faltando.length > 0) return
    salvar.mutate(
      {
        id: caixa?.id ?? null,
        dados: { titulo: titulo.trim(), modo, componenteId, sistemaId,
                 publicado: caixa?.publicado ?? false },
        modelos: [...escolhidos],
      },
      {
        onSuccess: () => {
          aoAvisar(caixa ? "Caixa alterada." : "Caixa criada.")
          aoFechar()
        },
        onError: (e) => aoAvisar(recado(e)),
      },
    )
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 11,
      padding: 14, borderTop: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
        <b style={{ fontSize: 12.5 }}>
          {caixa ? `Alterar a caixa ${caixa.id}` : "Nova caixa de fusíveis"}
        </b>
        <button onClick={aoFechar} style={{ ...campo, width: "auto", marginLeft: "auto",
                                            cursor: "pointer" }}>
          Fechar
        </button>
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={rotulo}>Título</span>
        <input value={titulo} onChange={(e) => setTitulo(e.target.value)}
               placeholder="Caixa do vão do motor" style={campo} />
      </label>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 190px" }}>
          <span style={rotulo}>Componente</span>
          <select value={componenteId} onChange={(e) => setComponenteId(Number(e.target.value))}
                  style={campo}>
            <option value={0}>escolher…</option>
            {componentes.itens.map((c) => (
              <option key={c.id} value={c.id}>
                {c.codigo ? `${c.codigo} — ${c.rotulo}` : c.rotulo}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 190px" }}>
          <span style={rotulo}>Sistema</span>
          <select value={sistemaId} onChange={(e) => setSistemaId(Number(e.target.value))}
                  style={campo}>
            <option value={0}>escolher…</option>
            {sistemas.itens.map((s) => (
              <option key={s.id} value={s.id}>{s.rotulo}</option>
            ))}
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 190px" }}>
          <span style={rotulo}>Modo</span>
          <select value={modo} onChange={(e) => setModo(Number(e.target.value))} style={campo}>
            {MODOS.map((m) => <option key={m.valor} value={m.valor}>{m.rotulo}</option>)}
          </select>
        </label>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={rotulo}>
          Modelos {escolhidos.size > 0 && `· ${escolhidos.size} escolhido(s)`}
        </span>
        <span style={{ fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
          A mesma caixa física existe uma vez por modelo. Sem modelo, ela fica cadastrada e
          não aparece para ninguém.
        </span>
        {modelos.isPending ? (
          <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>Carregando os modelos…</span>
        ) : (modelos.data ?? []).length === 0 ? (
          <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
            Este projeto não tem modelo cadastrado.
          </span>
        ) : (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 130, overflowY: "auto",
            padding: 6, borderRadius: 7, background: "var(--surface)",
            border: "1px solid var(--line)",
          }}>
            {(modelos.data ?? []).map((m) => (
              <label key={m.id} style={{
                display: "flex", alignItems: "center", gap: 5, fontSize: 11,
                padding: "3px 8px", borderRadius: 6, cursor: "pointer",
                background: escolhidos.has(m.id) ? "var(--brand-soft)" : "var(--surface2)",
                color: escolhidos.has(m.id) ? "var(--brand-on-soft)" : "var(--txt2)",
              }}>
                <input type="checkbox" checked={escolhidos.has(m.id)}
                       onChange={() => setEscolhidos((s) => {
                         const n = new Set(s)
                         n.has(m.id) ? n.delete(m.id) : n.add(m.id)
                         return n
                       })} />
                {m.descricao}
              </label>
            ))}
          </div>
        )}
      </div>

      {faltando.length > 0 && (
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>
          Falta {faltando.join(", ").replace(/, ([^,]*)$/, " e $1")}.
        </span>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          onClick={gravar}
          disabled={faltando.length > 0 || salvar.isPending}
          style={{
            ...campo, width: "auto", fontWeight: 600, cursor: faltando.length ? "default" : "pointer",
            background: faltando.length ? "var(--surface2)" : "var(--acao)",
            color: faltando.length ? "var(--txt3)" : "var(--acao-ink)",
            border: "1px solid " + (faltando.length ? "var(--line)" : "var(--acao)"),
          }}
        >
          {salvar.isPending ? "Gravando…" : caixa ? "Gravar" : "Criar a caixa"}
        </button>

        {caixa && (
          confirmando ? (
            <>
              <span style={{ fontSize: 11, color: "var(--erro)", lineHeight: 1.45, maxWidth: "44ch" }}>
                Apagar a caixa leva junto os elementos e as funções dela. Não tem lixeira.
              </span>
              <button
                onClick={() => excluir.mutate(caixa.id, {
                  onSuccess: () => { aoAvisar(`Caixa ${caixa.id} apagada.`); aoFechar() },
                  onError: (e) => aoAvisar(recado(e)),
                })}
                disabled={excluir.isPending}
                style={{ ...campo, width: "auto", cursor: "pointer",
                         color: "var(--erro)", fontWeight: 600 }}
              >
                {excluir.isPending ? "Apagando…" : "Apagar mesmo assim"}
              </button>
              <button onClick={() => setConfirmando(false)}
                      style={{ ...campo, width: "auto", cursor: "pointer" }}>
                Não
              </button>
            </>
          ) : (
            <button onClick={() => setConfirmando(true)}
                    style={{ ...campo, width: "auto", cursor: "pointer", color: "var(--erro)",
                             marginLeft: "auto" }}>
              Apagar a caixa
            </button>
          )
        )}
      </div>
    </div>
  )
}
